import { ApiError } from '@swarm/api-client';
import i18n from '../i18n';

/** Translated message for any error thrown by the API client (BE-015). */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const key = `errors.${error.code}`;
    if (i18n.exists(key)) {
      return i18n.t(key as never, error.params) as string;
    }
    // Generic text for validation codes like `treatment.notes.too_long`.
    const generic = `errors.generic.${error.code.split('.').pop() ?? ''}`;
    if (error.status === 422 && i18n.exists(generic)) {
      return i18n.t(generic as never, error.params) as string;
    }
    if (error.status >= 500 || error.status === 0) {
      return i18n.t('errors.server');
    }
  }
  if (error instanceof TypeError) {
    return i18n.t('errors.network');
  }
  return i18n.t('errors.unknown');
}

/** Error code of an API error, if any. */
export function errorCode(error: unknown): string | undefined {
  return error instanceof ApiError ? error.code : undefined;
}

/**
 * Splits an API error into a message for a form field (matched by error code
 * prefix, e.g. `password.` → `password`) and a general message.
 */
export function formErrors<F extends string>(
  error: unknown,
  fields: Record<F, string[]>,
): { field: Partial<Record<F, string>>; general?: string } {
  if (!error) return { field: {} };
  const code = errorCode(error);
  if (code) {
    for (const [field, prefixes] of Object.entries(fields) as [F, string[]][]) {
      if (prefixes.some((p) => code.startsWith(p))) {
        return { field: { [field]: errorMessage(error) } as Partial<Record<F, string>> };
      }
    }
  }
  return { field: {}, general: errorMessage(error) };
}
